import { IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import type { Globe } from "cobe";
import { useEffect, useRef, useState } from "react";
import {
  ARC_HEIGHT,
  drawGlobeFlows,
  GLOBE_TILT,
  ILLUSTRATIVE_MARKERS,
  MARKER_ELEVATION,
  MARKETPLACE_ROUTES,
} from "@/components/auth/auth-globe-flows";

export function AuthBrandScene() {
  const panelRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const flowRef = useRef<HTMLCanvasElement>(null);
  const playbackRef = useRef<(paused: boolean) => void>();
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [ready, setReady] = useState(false);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const panel = panelRef.current;
    const canvas = canvasRef.current;
    if (!panel || !canvas) return;
    const flowCanvas = flowRef.current;
    const flowContext = flowCanvas?.getContext("2d");

    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let globe: Globe | undefined;
    let disposed = false;
    let loading = false;
    let visible = false;
    let userPaused = false;
    let frame = 0;
    let previousTime = 0;
    let elapsed = 0;
    let phi = 1.1;
    let width = 0;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);

    function animate(time: number) {
      frame = requestAnimationFrame(animate);
      if (previousTime && time - previousTime < 1000 / 30) return;
      const delta = previousTime ? Math.min(time - previousTime, 64) : 0;
      previousTime = time;
      elapsed += delta;
      // The opening turn eases into a slow orbit; elapsed time avoids refresh-rate drift.
      const entrySpeed = Math.max(0, 1 - elapsed / 2600);
      phi += (delta / 1000) * (0.045 + 1.2 * entrySpeed ** 2);
      globe?.update({ phi });
      if (flowContext) drawGlobeFlows(flowContext, width, phi, elapsed);
    }

    function syncPlayback() {
      cancelAnimationFrame(frame);
      previousTime = 0;
      if (globe && visible && !document.hidden && !userPaused && !preference.matches) {
        frame = requestAnimationFrame(animate);
      }
    }

    function syncPreference() {
      setReducedMotion(preference.matches);
      if (preference.matches) flowContext?.clearRect(0, 0, width, width);
      syncPlayback();
    }

    async function initialize() {
      if (!canvas || globe || loading || disposed || !visible || width === 0) return;
      loading = true;
      try {
        const { default: createGlobe } = await import("cobe");
        if (disposed || !visible || width === 0) return;
        globe = createGlobe(canvas, {
          width,
          height: width,
          devicePixelRatio: pixelRatio,
          phi,
          theta: GLOBE_TILT,
          dark: 0,
          diffuse: 1.6,
          mapBrightness: 3.8,
          mapBaseBrightness: 0.08,
          baseColor: [0.24, 0.46, 0.78],
          glowColor: [0.71, 0.85, 1],
          markerColor: [0.78, 0.93, 1],
          arcColor: [0.53, 0.74, 0.94],
          mapSamples: 24000,
          markers: ILLUSTRATIVE_MARKERS,
          arcs: MARKETPLACE_ROUTES,
          arcWidth: 0.18,
          arcHeight: ARC_HEIGHT,
          markerElevation: MARKER_ELEVATION,
          context: { alpha: true, antialias: true },
        });
        // COBE returns a no-op renderer rather than throwing when WebGL is unavailable.
        const context = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
        if (!context || context.isContextLost()) {
          globe.destroy();
          globe = undefined;
          setFallback(true);
          return;
        }
        setFallback(false);
        setReady(true);
        syncPlayback();
      } catch {
        // A decorative WebGL failure must never prevent signing in.
        setReady(false);
        setFallback(true);
      } finally {
        loading = false;
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      width = canvas.clientWidth;
      if (width === 0) return;
      if (flowCanvas && flowContext) {
        flowCanvas.width = Math.round(width * pixelRatio);
        flowCanvas.height = Math.round(width * pixelRatio);
        flowContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        if (!preference.matches) drawGlobeFlows(flowContext, width, phi, elapsed);
      }
      globe?.update({ width, height: width });
      void initialize();
    });
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      void initialize();
      syncPlayback();
    });

    function handleContextLost(event: Event) {
      event.preventDefault();
      cancelAnimationFrame(frame);
      globe?.destroy();
      globe = undefined;
      setReady(false);
      setFallback(true);
      flowContext?.clearRect(0, 0, width, width);
    }

    playbackRef.current = (value) => {
      userPaused = value;
      syncPlayback();
    };
    syncPreference();
    resizeObserver.observe(canvas);
    intersectionObserver.observe(panel);
    preference.addEventListener("change", syncPreference);
    document.addEventListener("visibilitychange", syncPlayback);
    canvas.addEventListener("webglcontextlost", handleContextLost);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      preference.removeEventListener("change", syncPreference);
      document.removeEventListener("visibilitychange", syncPlayback);
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      playbackRef.current = undefined;
      globe?.destroy();
    };
  }, []);

  useEffect(() => playbackRef.current?.(paused), [paused]);

  return (
    <aside
      ref={panelRef}
      className="auth-brand-scene"
      data-paused={paused || reducedMotion}
      data-ready={ready}
      data-fallback={fallback}
      aria-label="AdPraxis — intelligent Amazon operations"
      lang="en"
    >
      <div className="auth-brand-atmosphere" aria-hidden="true">
        <div className="auth-brand-orbit auth-brand-orbit-one" />
        <div className="auth-brand-orbit auth-brand-orbit-two" />
        <div className="auth-brand-orbit auth-brand-orbit-three" />
        <div className="auth-brand-light" />
      </div>
      <div className="auth-brand-copy">
        <h1>
          Turn ad data
          <br />
          <span>into better decisions.</span>
        </h1>
        <p>From bids to keywords, put your Amazon advertising insights to work.</p>
      </div>
      <div className="auth-brand-globe-position" aria-hidden="true">
        <div className="auth-brand-globe-entry">
          <div className="auth-brand-globe-fallback" />
          <canvas ref={canvasRef} className="auth-brand-globe" />
          <canvas ref={flowRef} className="auth-brand-flows" />
        </div>
      </div>
      <div className="auth-brand-footer">
        <button
          type="button"
          className="auth-brand-motion-control"
          onClick={() => setPaused((value) => !value)}
          aria-label={
            reducedMotion
              ? "Animation disabled by reduced motion preference"
              : paused
                ? "Play globe animation"
                : "Pause globe animation"
          }
          title={
            reducedMotion ? "Reduced motion enabled" : paused ? "Play animation" : "Pause animation"
          }
          disabled={reducedMotion}
        >
          {paused || reducedMotion ? (
            <IconPlayerPlay size={14} stroke={1.5} />
          ) : (
            <IconPlayerPause size={14} stroke={1.5} />
          )}
        </button>
      </div>
    </aside>
  );
}
